const LEGACY_RAW_CHART_AGGREGATIONS = new Set([
  'raw',
  'raw data',
  'raw-data',
  'raw_data',
  'row data',
  'row-data',
  'row_data',
]);

const SUPPORTED_CHART_AGGREGATIONS = new Set([
  'mean',
  'min',
  'max',
  'min-max',
]);

const SUPPORTED_TABLE_AGGREGATIONS = new Set([
  ...SUPPORTED_CHART_AGGREGATIONS,
  'raw',
]);

const normalizeAggregationToken = (value) => String(value ?? '').trim().toLowerCase();

/**
 * Raw-data aggregation used to be exposed by chart settings even though the
 * history query pipeline is sampled. Keep old stored widgets working by
 * treating every legacy raw-data spelling as mean.
 *
 * Unknown/empty values also fall back to mean so a malformed saved widget can
 * never be interpolated as an unsupported Influx aggregate function.
 */
export const normalizeChartAggregation = (value) => {
  const normalized = normalizeAggregationToken(value);

  if (!normalized || LEGACY_RAW_CHART_AGGREGATIONS.has(normalized)) {
    return 'mean';
  }

  return SUPPORTED_CHART_AGGREGATIONS.has(normalized) ? normalized : 'mean';
};



export const normalizeTableAggregation = (value) => {
  const normalized = normalizeAggregationToken(value);

  if (LEGACY_RAW_CHART_AGGREGATIONS.has(normalized)) {
    return 'raw';
  }

  return SUPPORTED_TABLE_AGGREGATIONS.has(normalized) ? normalized : 'mean';
};

export const normalizeWidgetAggregation = (value, widgetType) => (
  String(widgetType || '').trim().toLowerCase() === 'table'
    ? normalizeTableAggregation(value)
    : normalizeChartAggregation(value)
);

export const isLegacyRawChartAggregation = (value) => (
  LEGACY_RAW_CHART_AGGREGATIONS.has(normalizeAggregationToken(value))
);

export const getChartAggregationOptions = (aggregations = []) => (
  (Array.isArray(aggregations) ? aggregations : [])
    .filter((option) => !isLegacyRawChartAggregation(option?.value))
    .filter((option) => SUPPORTED_CHART_AGGREGATIONS.has(normalizeAggregationToken(option?.value)))
    .map((option) => ({
      label: option.label,
      value: normalizeChartAggregation(option.value),
    }))
);

export const normalizeChartCurves = (curves = []) => (
  (Array.isArray(curves) ? curves : []).map((curve) => ({
    ...curve,
    aggregation: normalizeChartAggregation(curve?.aggregation),
  }))
);
